import type { CodiceContatore, Quadro, TipologiaImpianto } from '@praticheenergia/domain'
import { dec, somma, sottrai, moltiplica, type Dec } from './dec'

export type RifAliquota = string

/**
 * Insieme chiuso di sette operazioni. E deliberatamente piccolo: un DSL generale
 * andrebbe costruito e testato, e questo e il punto in cui un errore costa una sanzione.
 * Un anno d'imposta nuovo resta configurazione, non codice.
 */
export type Formula =
  | { op: 'costante'; valore: Dec }
  | { op: 'lettura'; contatore: CodiceContatore; tipologiaConsumo?: string }
  | { op: 'rigo'; quadro: Quadro; rigo: string }
  | { op: 'somma'; termini: Formula[] }
  | { op: 'differenza'; a: Formula; b: Formula }
  | { op: 'prodotto'; fattore: Formula; aliquota: RifAliquota }
  | { op: 'seTipologia'; tipologia: TipologiaImpianto; allora: Formula; altrimenti: Formula }

export interface ContestoCalcolo {
  readonly consumoPerContatore: Partial<Record<CodiceContatore, Dec>>
  /** Chiave `${codice}|${tipologiaConsumo}`. Serve al quadro E semestrale. */
  readonly consumoPerTipologia: Readonly<Record<string, Dec>>
  readonly righiCalcolati: Readonly<Record<string, Dec>>
  readonly aliquote: Readonly<Record<RifAliquota, Dec>>
  readonly tipologia: TipologiaImpianto
}

export class ErroreFormula extends Error {}

export function chiaveRigo(quadro: Quadro, rigo: string): string {
  return `${quadro}.${rigo}`
}

export function valuta(f: Formula, ctx: ContestoCalcolo): Dec {
  switch (f.op) {
    case 'costante':
      return f.valore

    case 'lettura': {
      // Un contatore assente vale zero: non ogni impianto ha ogni tipo di contatore.
      // Con la tipologia dichiarata si somma solo il sottoinsieme corrispondente:
      // dal 2026 il quadro E semestrale distingue i consumi per tipologia e mese.
      if (f.tipologiaConsumo !== undefined) {
        return ctx.consumoPerTipologia[`${f.contatore}|${f.tipologiaConsumo}`] ?? dec('0')
      }
      return ctx.consumoPerContatore[f.contatore] ?? dec('0')
    }

    case 'rigo': {
      const k = chiaveRigo(f.quadro, f.rigo)
      const v = ctx.righiCalcolati[k]
      // A differenza del contatore, un rigo mancante e un difetto delle regole:
      // significa che l'ordine di calcolo e sbagliato.
      if (v === undefined) throw new ErroreFormula(`rigo non calcolato: ${k}`)
      return v
    }

    case 'somma':
      return somma(...f.termini.map((t) => valuta(t, ctx)))

    case 'differenza':
      return sottrai(valuta(f.a, ctx), valuta(f.b, ctx))

    case 'prodotto': {
      const a = ctx.aliquote[f.aliquota]
      if (a === undefined) throw new ErroreFormula(`aliquota sconosciuta: ${f.aliquota}`)
      return moltiplica(valuta(f.fattore, ctx), a)
    }

    case 'seTipologia':
      return ctx.tipologia === f.tipologia ? valuta(f.allora, ctx) : valuta(f.altrimenti, ctx)
  }
}
